'use client';

import { HeaderAccount } from 'components/pcc-components';
import { useTranslations } from 'next-intl';
import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { returnFocusToNavItem } from '../../utils/common-utils';
import { useNavigationDrawers } from '../navigation-drawers-context';
import { DealerLocatorModal } from 'components/dealer-locator';
import { AccountDrawerWrapper } from './account-drawer-wrapper';
import { StoreAddress } from 'components/dealer-locator/types';
import { UserConsentModal } from 'components/user-consent-modal';
import { UserConsentModalProps } from 'components/user-consent-modal/types';

type AccountClientItemProps = {
  isLoggedIn: boolean;
  fullName: string;
  dealerCustomerNumber: string;
  isCatCsrUser?: boolean;
  isInstantAccessUser?: boolean;
  isMobile?: boolean;
  storeAddress?: StoreAddress;
  restrictedDealerStores?: string[];
} & UserConsentModalProps;

export const AccountClient: FC<PropsWithChildren<AccountClientItemProps>> = ({
  isLoggedIn,
  fullName,
  dealerCustomerNumber,
  children,
  isInstantAccessUser,
  isCatCsrUser = false,
  isMobile = false,
  dealerName,
  dealerStoreId,
  storeAddress,
  storeLocationId,
  storeLocationName,
  restrictedDealerStores
}) => {
  const accountID = isMobile ? 'headerAccountMobile' : 'headerAccount';
  const { accountDrawer } = useNavigationDrawers();
  const t = useTranslations();
  const label = isLoggedIn ? fullName : t('SIGN_IN');
  const drawerWasOpen = useRef(false);

  useEffect(() => {
    if (!accountDrawer?.isDrawerOpen && drawerWasOpen.current) {
      returnFocusToNavItem(`#${accountID}`);
      drawerWasOpen.current = false;
    } else if (accountDrawer?.isDrawerOpen && !drawerWasOpen.current) {
      drawerWasOpen.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountDrawer?.isDrawerOpen]);

  return (
    <>
      <HeaderAccount
        id={accountID}
        label={label}
        dealerCustomerNumber={dealerCustomerNumber}
        isCurrent={accountDrawer?.isDrawerOpen}
        displayHeaderAccountLabel={!isMobile}
        handleDrawerProps={accountDrawer?.initiatorProps}
        roleLabel={`(${t('CONTACT_US_CONF1')})`}
        showRole={isCatCsrUser}
      />
      <AccountDrawerWrapper>{children}</AccountDrawerWrapper>
      <DealerLocatorModal
        autoSearch={!isLoggedIn || isInstantAccessUser}
        isRegistration={!isCatCsrUser}
        storeAddress={storeAddress}
        restrictedDealerStores={restrictedDealerStores}
        isCatCSR={isCatCsrUser}
      />
      <UserConsentModal
        dealerName={dealerName}
        dealerStoreId={dealerStoreId}
        storeLocationId={storeLocationId}
        storeLocationName={storeLocationName}
      />
    </>
  );
};
